/**
 * Shared single-facet filter chips (pre-deployment polish, 2026-07-29).
 *
 * Extracted from three hand-rolled copies (shift-requests / notifications /
 * payouts) so every "pick one value for this facet" filter renders and behaves
 * identically: pill buttons, `aria-pressed` on the active one, forest fill when
 * selected, in an ARIA-labelled group.
 *
 * When to use which filter primitive (the admin standard):
 *   - <AdminFilterChips>  — ONE enumerable facet, one-click switch (this).
 *   - <AdminListFilters>  — multiple fields at once (status + date + free text).
 *   - underline tabs       — switching data SETS (e.g. Rates sub-resources), not
 *                            filtering rows.
 *
 * Page-level concerns (resetting page to 1 on change, URL-sync) stay in the
 * caller's onChange — this component only reports the picked value.
 */
export interface AdminFilterChipsProps<T extends string> {
  value: T;
  /** `[value, label]` tuples — matches the existing call sites' `as const` shape. */
  options: readonly (readonly [T, string])[];
  onChange: (value: T) => void;
  /** Accessible name for the group (e.g. "Filter payouts by status"). */
  ariaLabel: string;
  /** Stable test-id prefix → `{prefix}-chips` container + `{prefix}-{value}` chips. */
  testIdPrefix?: string;
}

export function AdminFilterChips<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  testIdPrefix,
}: AdminFilterChipsProps<T>) {
  return (
    <div
      className="flex flex-wrap gap-2"
      role="group"
      aria-label={ariaLabel}
      data-testid={testIdPrefix ? `${testIdPrefix}-chips` : undefined}
    >
      {options.map(([key, label]) => (
        <button
          key={key}
          type="button"
          aria-pressed={value === key}
          onClick={() => onChange(key)}
          data-testid={testIdPrefix ? `${testIdPrefix}-${key}` : undefined}
          className={
            value === key
              ? 'rounded-md bg-forest text-paper px-3 py-1 text-xs font-medium'
              : 'rounded-md border border-border bg-paper-warm text-ink-muted px-3 py-1 text-xs hover:text-forest'
          }
        >
          {label}
        </button>
      ))}
    </div>
  );
}
